import { Tabs } from 'expo-router';
import { useLayoutSwitcher } from '@/features/global/hooks/useLayoutSwitcher';
import { ImageSourcePropType, useColorScheme, View } from 'react-native';
import { Icon, IconProps } from '@/features/global/components/icon/Icon';
import { useTranslation } from 'react-i18next';
import { ComponentType, useMemo } from 'react';
import { createMainMenu } from '~/layout/defs/createMainMenu';
import { NativeTabs } from 'expo-router/unstable-native-tabs';
import { colors } from '@/lib/colors';

import { hasLiquidGlassTabBar } from '@/lib/utils';
import { TabBar, TabBarProps } from '~/layout/components/tab-bar/TabBar';

const tabBar = (props: TabBarProps) => <TabBar {...props} />;

const createIcon =
  ({
    icon,
  }: {
    icon: ComponentType<{ size?: number; color?: string }> | IconProps['name'];
  }) =>
  ({ color }: { focused: boolean; color: string }) => {
    if (typeof icon === 'string') {
      return (
        <View>
          <Icon name={icon} size={24} color={color} />
        </View>
      );
    }
    const IconComponent = icon;
    return (
      <View>
        <IconComponent size={24} color={color} />
      </View>
    );
  };

export default function TabsLayout() {
  useLayoutSwitcher();
  const { t } = useTranslation('navigation');
  const colorScheme = useColorScheme();
  const menuItems = useMemo(() => createMainMenu(t, 'tabBar'), [t]);
  const visibleItems = menuItems;
  if (hasLiquidGlassTabBar()) {
    return (
      <NativeTabs tintColor={colors[colorScheme ?? 'light'].accent}>
        {visibleItems
          .filter((item) => !item.options?.hidden)
          .map((item) => (
            <NativeTabs.Trigger key={item.id} name={item.name}>
              <NativeTabs.Trigger.Label>{item.label}</NativeTabs.Trigger.Label>
              {item.icon &&
                typeof item.icon === 'string' &&
                (() => {
                  const iconSrcMap: Record<
                    string,
                    {
                      default: ImageSourcePropType;
                      selected: ImageSourcePropType;
                    }
                  > = {
                    House: {
                      default: require('../../../assets/icons/house-inactive.png'),
                      selected: require('../../../assets/icons/house-active.png'),
                    },
                    Dumbbell: {
                      default: require('../../../assets/icons/dumbbell-inactive.png'),
                      selected: require('../../../assets/icons/dumbbell-active.png'),
                    },
                    Utensils: {
                      default: require('../../../assets/icons/utensils-inactive.png'),
                      selected: require('../../../assets/icons/utensils-active.png'),
                    },
                    CircleUser: {
                      default: require('../../../assets/icons/circle-user-inactive.png'),
                      selected: require('../../../assets/icons/circle-user-active.png'),
                    },
                    Bot: {
                      default: require('../../../assets/icons/bot-inactive.png'),
                      selected: require('../../../assets/icons/bot-active.png'),
                    },
                  };
                  return iconSrcMap[item.icon] ? (
                    <NativeTabs.Trigger.Icon
                      src={iconSrcMap[item.icon]}
                      renderingMode="template"
                    />
                  ) : null;
                })()}
            </NativeTabs.Trigger>
          ))}
        <NativeTabs.Trigger name="chat" role="search">
          <NativeTabs.Trigger.Icon
            src={{
              default: require('../../../assets/icons/bot-inactive.png'),
              selected: require('../../../assets/icons/bot-active.png'),
            }}
            renderingMode="template"
          />
        </NativeTabs.Trigger>
      </NativeTabs>
    );
  }
  return (
    <Tabs screenOptions={{ headerShown: false }} tabBar={tabBar}>
      {visibleItems.map((item) => (
        <Tabs.Screen
          key={item.id}
          name={item.name}
          options={{
            ...item.options,
            title: item.label,
            tabBarLabel: item.label,
            ...(item.icon && { tabBarIcon: createIcon({ icon: item.icon }) }),
          }}
        />
      ))}
      <Tabs.Screen
        name="settings"
        options={{
          hidden: true,
        }}
      />
      <Tabs.Screen
        name="chat"
        options={{
          hidden: true,
        }}
      />
      <Tabs.Screen
        name="upgrade"
        options={{
          hidden: true,
        }}
      />
    </Tabs>
  );
}
